import { useCallback } from 'react';
import type { FormInstance } from 'antd';
import type { MutableRefObject } from 'react';
import dayjs from 'dayjs';
import type { PlantingPayload, PlantingUpdatePayload } from '../../../../../models/Planting';
import { toastError } from '../../../../../utils/toast';
import type { LockedLocationIds, PlantingFormModalValues, PlantingFormModalValuesLocation } from '../types';

interface UseFormSubmitOptions {
  form: FormInstance<PlantingFormModalValues>;
  lockedLocation: LockedLocationIds | null;
  initialLocationsRef: MutableRefObject<PlantingFormModalValuesLocation[]>;
  submitLocations?: boolean;
  onSubmit: (payload: PlantingPayload | PlantingUpdatePayload) => Promise<void> | void;
}

/**
 * Encapsulates location resolution logic and form submission.
 */
export const useFormSubmit = ({
  form,
  lockedLocation,
  initialLocationsRef,
  submitLocations = true,
  onSubmit,
}: UseFormSubmitOptions) => {
  const resolveLocationsForSubmit = useCallback(
    (submitted?: PlantingFormModalValuesLocation[]): PlantingFormModalValuesLocation[] => {
      const submittedList = Array.isArray(submitted) ? submitted : [];
      const fallbackList = initialLocationsRef.current ?? [];

      if (submittedList.length > 0) {
        return submittedList.map((location, index) => {
          const fallback = fallbackList[index];
          const effectiveBedId =
            location.bedId ??
            fallback?.bedId ??
            (lockedLocation && index === 0 ? lockedLocation.bedId : undefined);

          if (effectiveBedId === undefined || effectiveBedId === null) {
            throw new Error('Не удалось определить грядку для посадки');
          }

          const fromDateSource = location.fromDate ?? fallback?.fromDate ?? dayjs();
          const toDateSource = location.toDate ?? fallback?.toDate ?? null;

          return {
            id: location.id ?? fallback?.id ?? undefined,
            bedId: effectiveBedId,
            fromDate: fromDateSource,
            toDate: toDateSource,
          };
        });
      }

      if (lockedLocation && fallbackList.length > 0) {
        return fallbackList.map((location, index) => {
          const fallback = fallbackList[index];
          const effectiveBedId =
            fallback?.bedId ?? (lockedLocation && index === 0 ? lockedLocation.bedId : undefined);

          if (effectiveBedId === undefined || effectiveBedId === null) {
            throw new Error('Не удалось определить грядку для посадки');
          }

          return {
            id: fallback?.id ?? undefined,
            bedId: effectiveBedId,
            fromDate: fallback?.fromDate ?? dayjs(),
            toDate: fallback?.toDate ?? null,
          };
        });
      }

      return [];
    },
    [lockedLocation, initialLocationsRef],
  );

  const handleSubmit = useCallback(async () => {
    try {
      const values = await form.validateFields();
      const resolvedLocations = submitLocations
        ? resolveLocationsForSubmit(values.locations)
        : [];

      if (submitLocations && (!resolvedLocations || resolvedLocations.length === 0)) {
        throw new Error('Не указаны локации посадки');
      }

      const quantity = values.quantity && values.quantity > 0 ? values.quantity : 1;
      const plantingType = quantity > 1 ? 'batch' : 'single';

      const payload: PlantingPayload | PlantingUpdatePayload = {
        cropId: values.cropId!,
        plantingType,
        quantity: quantity > 1 ? quantity : undefined,
        originDate: values.originDate ? values.originDate.format('YYYY-MM-DD') : null,
        originType: values.originType || undefined,
      };

      if (submitLocations) {
        payload.locations = resolvedLocations.map((location) => {
          if (location.bedId === undefined || location.bedId === null) {
            throw new Error('Не удалось определить грядку для посадки');
          }
          const fromDateValue = location.fromDate ?? dayjs();
          const toDateValue = location.toDate ?? null;

          return {
            id: location.id ?? undefined,
            bedId: location.bedId,
            fromDate: fromDateValue.format('YYYY-MM-DD'),
            toDate: toDateValue ? toDateValue.format('YYYY-MM-DD') : null,
          };
        });
      }

      await onSubmit(payload);
    } catch (error: any) {
      if (error?.errorFields) {
        return; // Ant Design validation — errors shown in form
      }
      toastError(error);
    }
  }, [form, resolveLocationsForSubmit, submitLocations, onSubmit]);

  return { handleSubmit };
};
